import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ShieldCheck, Truck, Lock, PackageCheck, RotateCcw, Star, CheckCircle2, AlertTriangle, CreditCard, Milestone } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API, money, abs } from "./shared";
import { ShiprocketShipmentPanel } from "./Dispatch";

// ============================================================
// BUYER: Live production milestone widget (read-only mirror of
// the supplier's 11-stage timeline).
// ============================================================
export function BuyerMilestoneTimeline({ jobId, status }) {
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/milestones`);
      setTimeline(data);
      setError("");
    } catch (e) {
      // Silent no-render if the buyer isn't linked to this job (403) or job doesn't exist
      if (e?.response?.status === 403) return;
      setError(e?.response?.data?.detail || "Could not load milestones.");
    }
  };

  useEffect(() => {
    load();
    // Poll every 20s while the job is in production so the buyer sees updates live
    if (["awarded", "dispatched"].includes(status)) {
      const t = setInterval(load, 20000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, status]);

  // Skip rendering entirely on non-awarded jobs
  if (!timeline && !error) return null;
  if (!timeline || timeline?.stages?.length === 0) return null;

  return (
    <>
      <div className="drawer-section-title" style={{ marginTop: 22 }}>
        <h3>Production progress</h3>
        <span data-testid="buyer-ms-progress-text">
          {timeline.percent}% · {timeline.completed_count} / {timeline.total} milestones
        </span>
      </div>
      <div className="buyer-ms-widget" data-testid={`buyer-ms-widget-${jobId}`}>
        <div className="buyer-ms-bar">
          <div className="buyer-ms-bar-fill" style={{ width: `${timeline.percent}%` }} data-testid="buyer-ms-progress-bar" />
        </div>
        <ol className="ms-timeline compact" data-testid={`buyer-ms-timeline-${jobId}`}>
          {timeline.stages.map((s, i) => (
            <li key={s.code} className={`ms-step ms-${s.state}`} data-testid={`buyer-ms-step-${s.code}`}>
              <span className="ms-marker">
                {s.state === "done" ? <CheckCircle2 size={14}/> : (s.state === "current" ? <Milestone size={14}/> : <span className="ms-dot"/>)}
              </span>
              <div className="ms-step-body">
                <b>{i + 1}. {s.label}</b>
                {s.state === "done" && s.updated_at && (
                  <span className="ms-meta">Updated {new Date(s.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}{s.updated_by ? ` · by ${s.updated_by}` : ""}</span>
                )}
                {s.state === "done" && s.note && <span className="ms-note">"{s.note}"</span>}
                {s.state === "current" && <span className="ms-meta ms-next">Awaiting supplier update</span>}
              </div>
            </li>
          ))}
        </ol>
        {error && <div className="form-error" data-testid="buyer-ms-error">{error}</div>}
      </div>
    </>
  );
}


export function BuyerToolkit({ job, refresh, setModal }) {
  const { user } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [funding, setFunding] = useState(false);
  const [fundError, setFundError] = useState("");
  useEffect(() => { axios.get(`${API}/jobs/${job.id}/tracking`).then(r => setTracking(r.data)); }, [job.id, job.status]);

  const fundEscrow = async () => {
    setFunding(true);
    setFundError("");
    try {
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/razorpay/escrow/order/${job.id}`).then(r => r.data),
      ]);
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack Escrow",
        description: `Escrow for ${job.title || job.id}`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { kind: "escrow", job_id: job.id },
      });
      await axios.post(`${API}/razorpay/verify`, { ...resp, job_id: job.id });
      await refresh();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") setFundError("Payment cancelled. You can fund escrow any time.");
      else setFundError(e?.description || e?.response?.data?.detail || "Escrow payment failed. Please try again.");
    } finally {
      setFunding(false);
    }
  };

  const confirmDelivery = async () => {
    if (!window.confirm("Confirm delivery? This will release escrow payout to the supplier.")) return;
    await axios.post(`${API}/jobs/${job.id}/deliver`);
    await refresh();
  };
  const simulateCourier = async () => {
    await axios.post(`${API}/jobs/${job.id}/courier-webhook?event=delivered`);
    await refresh();
  };
  const reorder = async () => {
    const qty = window.prompt(`Reorder ${job.title}\n\nQuantity (leave blank to keep ${job.quantity})`, "");
    const deadline = window.prompt(`Deadline (YYYY-MM-DD, leave blank to keep)`, "");
    const payload = {};
    if (qty && !isNaN(Number(qty))) payload.quantity = Number(qty);
    if (deadline && /^\d{4}-\d{2}-\d{2}$/.test(deadline)) payload.deadline = deadline;
    try {
      const { data } = await axios.post(`${API}/jobs/${job.id}/reorder`, payload);
      await refresh();
      const supplierHint = data.preferred_supplier_id
        ? ` Your previous supplier is flagged as preferred — they'll see the RFQ first.`
        : "";
      toast.success(`Reorder queued · ${data.title}.${supplierHint}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };
  const paymentStatus = job.payment_status || (job.payout_status === "released" ? "Released" : "Awaiting escrow");
  const escrowFunded = job.escrow_gateway === "razorpay" && paymentStatus === "In Escrow";
  const needsRazorpayFunding = ["awarded"].includes(job.status) && !escrowFunded && paymentStatus !== "Released";
  return (
    <>
      <div className="drawer-callout">
        <ShieldCheck size={17}/>
        <div>
          <b>Escrow · {money(job.awarded_price)} · <span className={paymentStatus === "Released" ? "pay-released" : paymentStatus === "In Escrow" ? "pay-released" : "pay-held"}>{paymentStatus}</span></b>
          <span>
            {job.escrow_gateway === "razorpay"
              ? `Razorpay payment · ${job.razorpay_payment_id || job.razorpay_order_id || "pending"}`
              : `Razorpay order ${job.razorpay_order_id || "not funded"}`}
            {/* Platform commission hidden from buyer view — buyers never see
                how much of their payment goes to the platform vs the supplier.
                The admin + supplier views continue to show it. */}
          </span>
        </div>
      </div>

      {needsRazorpayFunding && (
        <div className="drawer-callout" data-testid="fund-escrow-callout" style={{ background: "#fff8e6", borderColor: "#f5c46b" }}>
          <AlertTriangle size={17}/>
          <div style={{ flex: 1 }}>
            <b>Fund escrow to unlock production</b>
            <span>Your award will not release press-ready files to the supplier until escrow is captured.</span>
            <button
              data-testid="fund-escrow-button"
              className="primary-btn"
              style={{ marginTop: 10 }}
              disabled={funding}
              onClick={fundEscrow}
            >
              {funding ? "Opening Razorpay…" : `Fund escrow · ${money(job.awarded_price)}`} <CreditCard size={15}/>
            </button>
            {fundError && <p className="stripe-mock-note" data-testid="fund-escrow-error" style={{ marginTop: 8 }}><Lock size={13}/> {fundError}</p>}
          </div>
        </div>
      )}

      <BuyerMilestoneTimeline jobId={job.id} status={job.status} />

      <div className="drawer-section-title">
        <h3>Delivery tracking</h3>
        <span>
          {job.tracking_number
            ? <>{job.carrier} · {job.tracking_url ? <a href={job.tracking_url} target="_blank" rel="noreferrer" data-testid="tracking-link" className="track-link">{job.tracking_number} ↗</a> : <span data-testid="tracking-link">{job.tracking_number}</span>}</>
            : "Awaiting dispatch"}
        </span>
      </div>
      {job.shiprocket?.awb && (
        <ShiprocketShipmentPanel shiprocket={job.shiprocket} hideLabel/>
      )}
      <div className="tracking-timeline" data-testid="tracking-timeline">
        {tracking?.events?.map((e, i) => (
          <div key={i} className={`tl-row ${e.done ? "done" : ""}`}>
            <div className="tl-dot"/>
            <div><b>{e.stage}</b><span>{e.at ? new Date(e.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "Pending"}</span></div>
          </div>
        ))}
      </div>

      <div className="toolkit-actions">
        <button data-testid="proof-open-button" className="tool-btn" onClick={() => setModal("proof")}>
          <FileText size={15}/> Digital proof
        </button>
        {["completed", "dispatched", "closed", "awarded", "paid"].includes(job.status) && (
          <button data-testid="reorder-button" className="tool-btn" onClick={reorder}>
            <RotateCcw size={15}/> Reorder
          </button>
        )}
        {job.status === "dispatched" && (() => {
          // Auto-release countdown so the buyer isn't surprised when funds move.
          // Mirrors backend AUTO_RELEASE_DAYS=7 — if we shift the window, update
          // both ends. Dispute or admin-hold suspends auto-release.
          const dispatchedAt = job.dispatched_at ? new Date(job.dispatched_at) : null;
          const releaseAt = dispatchedAt ? new Date(dispatchedAt.getTime() + 7 * 24 * 60 * 60 * 1000) : null;
          const daysLeft = releaseAt ? Math.max(0, Math.ceil((releaseAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000))) : null;
          return (
            <>
              {releaseAt && !job.is_disputed && (
                <div data-testid="auto-release-notice" style={{background:"#FEF3C7",border:"1px solid #FDE68A",color:"#78350F",padding:"10px 12px",borderRadius:8,fontSize:12,lineHeight:1.55,marginBottom:4}}>
                  <b>⏰ Please confirm receipt by {releaseAt.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}.</b>{" "}
                  {daysLeft > 0
                    ? `If we don't hear from you in ${daysLeft} day${daysLeft === 1 ? "" : "s"}, the payout will auto-release to the supplier.`
                    : "The payout is now eligible for auto-release — our daily cron will complete it shortly if you don't confirm or dispute."}
                </div>
              )}
              <button data-testid="confirm-delivery-button" className="tool-btn primary" onClick={confirmDelivery}>
                <PackageCheck size={15}/> Confirm delivery & release payout
              </button>
              <button data-testid="courier-webhook-button" className="tool-btn" onClick={simulateCourier} title="Simulate a UPS/FedEx courier callback that auto-confirms delivery">
                <Truck size={15}/> Simulate courier confirmation
              </button>
            </>
          );
        })()}
        {["awarded","dispatched","completed"].includes(job.status) && !job.is_disputed && (
          <button data-testid="raise-dispute-button" className="tool-btn danger" onClick={() => setModal("dispute")}>
            <AlertTriangle size={15}/> Raise a dispute
          </button>
        )}
        {job.status === "completed" && !job.buyer_rating && (
          <button data-testid="rate-delivery-button" className="tool-btn primary" onClick={() => setModal("rate")}>
            <Star size={15}/> Rate this delivery
          </button>
        )}
        {job.status === "completed" && job.buyer_rating && (
          <div className="tool-btn done" data-testid="already-rated">
            <Star size={15}/> Rated · {job.buyer_rating.stars}★
          </div>
        )}
        {job.status === "completed" && (
          <div className="tool-btn done" data-testid="payout-released-pill">
            <CheckCircle2 size={15}/> Delivered · Supplier payout released
          </div>
        )}
      </div>
    </>
  );
}

export function SupplierWonPanel({ job, setModal }) {
  const [assets, setAssets] = useState([]);
  useEffect(() => {
    axios.get(`${API}/jobs/${job.id}/assets?supplier_id=${job.awarded_supplier_id}`)
      .then(r => setAssets(r.data.assets || []))
      .catch(() => {});
  }, [job.id, job.awarded_supplier_id]);
  return (
    <>
      <div className="drawer-callout ok">
        <CheckCircle2 size={17}/>
        <div>
          <b>You won this auction — {money(job.awarded_price)}</b>
          <span>Escrow secured. Press-ready artwork & dielines have been released below.</span>
        </div>
      </div>

      {assets.length > 0 && (
        <>
          <div className="drawer-section-title" style={{ marginTop: 22 }}>
            <h3>Press-ready assets</h3>
            <span>Released {new Date(job.assets_released_at || Date.now()).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
          </div>
          <div className="asset-list" data-testid="press-assets-list">
            {assets.map((a, i) => (
              <a
                key={i}
                href={abs(a.url)}
                target="_blank"
                rel="noreferrer"
                data-testid={`asset-${i}`}
                className="asset-row"
              >
                <div className="asset-icon"><FileText size={16}/></div>
                <div className="asset-info">
                  <b>{a.name}</b>
                  <span>{a.type.toUpperCase()} · {a.size}</span>
                </div>
                <span className="asset-download">Download ↗</span>
              </a>
            ))}
          </div>
        </>
      )}

      {job.shiprocket?.awb && (
        <>
          <div className="drawer-section-title" style={{ marginTop: 22 }}>
            <h3>Blind shipment</h3>
            <span>Supplier identity scrubbed · GoPrintPack brand</span>
          </div>
          <ShiprocketShipmentPanel shiprocket={job.shiprocket} />
        </>
      )}

      <div className="toolkit-actions">
        {["awarded", "dispatched"].includes(job.status) && job.delivery_address && (
          <a data-testid="print-blind-label-button" className="tool-btn" href={`${API}/jobs/${job.id}/blind-label`} target="_blank" rel="noreferrer">
            <FileText size={15}/> Print blind label
          </a>
        )}
        {job.status === "awarded" && (() => {
          // Two gates for "Mark dispatched":
          //   1) At least one pre-dispatch production milestone has been posted
          //   2) For jobs that need artwork, buyer must have approved the soft proof
          // Backend enforces both — frontend mirrors them for a helpful UI.
          const PRE_DISPATCH = ["awarded_accepted","prepress_verify","soft_proof_sent","soft_proof_approved","prepress_ready","in_press","postpress_finishing","qc_passed","packed"];
          const done = new Set((job.production_milestones || []).map(m => m.code));
          const hasPreDispatch = PRE_DISPATCH.some(c => done.has(c));
          const artworkRequired = job.artwork_required !== false; // undefined → required (legacy)
          const proofApproved = job.proof_approved === true;
          const proofGateBlocked = artworkRequired && !proofApproved;
          if (hasPreDispatch && !proofGateBlocked) {
            return (
              <button data-testid="dispatch-button" className="tool-btn primary" onClick={() => setModal("dispatch")}>
                <Truck size={15}/> Mark dispatched
              </button>
            );
          }
          // Compose a clear "why am I blocked?" hint — show whichever gate is open.
          const hints = [];
          if (proofGateBlocked) {
            hints.push(
              <div key="proof" data-testid="dispatch-proof-hint" style={{fontSize:11.5,color:"#991B1B",background:"#FEE2E2",border:"1px solid #FCA5A5",padding:"8px 10px",borderRadius:8,lineHeight:1.5}}>
                <b>Digital proof approval pending.</b> {job.proof_url
                  ? "Buyer hasn't approved your soft proof yet — ping them on job chat."
                  : "Upload a soft proof on the job page and wait for the buyer to approve it. Milestones past 'Proof Approved' are also locked until then."}
              </div>
            );
          }
          if (!hasPreDispatch) {
            hints.push(
              <div key="milestone" data-testid="dispatch-milestone-hint" style={{fontSize:11.5,color:"#92400E",background:"#FEF3C7",border:"1px solid #FDE68A",padding:"8px 10px",borderRadius:8,lineHeight:1.5}}>
                <b>Post a production milestone first.</b> Open the milestone tracker on this job and log at least one update (e.g. "In Press" or "Packed") so the buyer sees progress before you dispatch.
              </div>
            );
          }
          return (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button
                data-testid="dispatch-button-blocked"
                className="tool-btn"
                disabled
                title={proofGateBlocked ? "Buyer must approve the digital proof before dispatch" : "Post at least one production milestone before marking dispatched"}
                style={{opacity:0.55, cursor:"not-allowed"}}
              >
                <Truck size={15}/> Mark dispatched
              </button>
              {hints}
            </div>
          );
        })()}
        {job.status === "dispatched" && (
          <div className="tool-btn">Awaiting buyer's delivery confirmation…</div>
        )}
        {job.status === "completed" && (
          <div className="tool-btn done"><CheckCircle2 size={15}/> Delivered · Payout {money(job.payout_amount)} released</div>
        )}
        {["awarded","dispatched","completed"].includes(job.status) && !job.is_disputed && (
          <button data-testid="printer-raise-dispute" className="tool-btn danger" onClick={() => setModal("dispute")}>
            <AlertTriangle size={15}/> Dispute this order
          </button>
        )}
      </div>
    </>
  );
}

// ============================================================
// INVOICES PANEL — MoR 2-tier tax invoices (auto on dispatched)
// ============================================================
export function InvoicesPanel({ job, role }) {
  const [invoices, setInvoices] = useState(null);
  const show = ["dispatched", "completed"].includes(job.status) || !!job.invoice_customer_id;

  useEffect(() => {
    if (!show) return;
    axios.get(`${API}/jobs/${job.id}/invoices`)
      .then(r => setInvoices(r.data.invoices || []))
      .catch(() => setInvoices([]));
  }, [job.id, show]);

  if (!show) return null;
  if (invoices === null) return <div className="drawer-callout" data-testid="invoices-loading" style={{marginTop:14}}>Loading invoices…</div>;
  if (invoices.length === 0) {
    return (
      <div className="drawer-callout" data-testid="invoices-empty" style={{marginTop:14,background:"#FEF3C7",border:"1px solid #FCD34D",padding:12,borderRadius:8,fontSize:13}}>
        Invoices will be generated the moment the supplier marks the job dispatched.
      </div>
    );
  }

  const mine = invoices.filter(i => {
    if (role === "admin") return true;
    if (role === "buyer") return i.type === "platform_to_customer";
    return i.type === "supplier_to_platform";
  });
  const others = invoices.filter(i => !mine.includes(i));

  return (
    <div data-testid="invoices-panel" style={{marginTop:16}}>
      <h4 style={{margin:"6px 0 10px",fontSize:13,color:"#111827"}}>Tax invoices</h4>
      <div style={{display:"grid",gap:8}}>
        {mine.map(inv => <InvoiceRow key={inv.id} inv={inv} highlight role={role}/>)}
        {role === "admin" && others.map(inv => <InvoiceRow key={inv.id} inv={inv} role={role}/>)}
      </div>
    </div>
  );
}

export function InvoiceRow({ inv, highlight, role }) {
  const isCustomer = inv.type === "platform_to_customer";
  const label = isCustomer ? "Tax invoice · Platform → Customer" : "Supplier invoice · Supplier → Platform";
  const otherParty = isCustomer ? inv.buyer?.name : inv.seller?.name;
  const api = axios.defaults.baseURL || "";
  const href = `${API}/invoices/${inv.id}/pdf`;
  return (
    <div
      data-testid={`invoice-row-${inv.id}`}
      style={{
        border: highlight ? "1.5px solid #10b981" : "1px solid #e5e7eb",
        background: highlight ? "#F0FDF4" : "#fff",
        padding: 12, borderRadius: 8,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}
    >
      <FileText size={18} color={isCustomer ? "#0369a1" : "#7c3aed"}/>
      <div style={{flex:1,minWidth:220}}>
        <div style={{fontSize:12,fontWeight:600,color:"#111827"}}>{inv.invoice_number}</div>
        <div style={{fontSize:11,color:"#6b7280"}}>{label}{otherParty ? ` · ${otherParty}` : ""}</div>
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{fontSize:12,fontWeight:600,color:"#059669"}}>₹{inv.grand_total_inr?.toLocaleString("en-IN")}</div>
        <div style={{fontSize:10,color:"#9ca3af"}}>GST {inv.gst_pct}% · {inv.is_intra_state ? "intra-state" : "inter-state"}</div>
      </div>
      {inv.tds_amount_inr != null && (
        <div style={{textAlign:"right",fontSize:10,color:"#B45309"}} title="TDS 194-O">
          −₹{inv.tds_amount_inr?.toLocaleString("en-IN")} TDS<br/>
          <b style={{color:"#065F46"}}>Net ₹{inv.net_payable_inr?.toLocaleString("en-IN")}</b>
        </div>
      )}
      <a
        data-testid={`invoice-pdf-${inv.id}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="secondary-btn"
        style={{textDecoration:"none",fontSize:12}}
      >
        Download PDF
      </a>
    </div>
  );
}
